/* eslint-disable react/jsx-props-no-spreading */
import {
  Checkbox,
  Divider,
  FormControlOwnProps,
  ListItemText,
  MenuProps,
  OutlinedInput,
} from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import { Button, colors } from '@resiliatech/birdsong'
import { GoalCategory, UserSurveyPriority } from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { splitAtUppercaseAndAddSpace, toTitleCase } from 'Utils/formatters'
import { useAppState } from 'Context/AppContext/AppContext'
import { DashboardFilters as DashboardFiltersType, FilterArgs } from '../types'
import {
  ClearFiltersContainer,
  Filters,
  TaskFiltersContainer,
} from './DashboardFilters.styled'
import { PRIORTIES_FILTER_TRIGGER } from '../utils'
import {
  dispatchImpactReportCategoryFilterClicked,
  dispatchImpactReportFilterByPriorityClicked,
  dispatchImpactReportGoalTitleFilterClicked,
  dispatchImpactReportTeamFilterCleared,
  dispatchImpactReportTeamFilterClicked,
} from '../analytics.utils'

const goalCategories = Object.entries(GoalCategory).filter(
  ([, value]) => value !== GoalCategory.None
)

const SELECT_WIDTH = '200px'
const inputLabelStyles = { color: colors.copy.paragraphDark }
const formControlStyles = { minWidth: SELECT_WIDTH }
const formControlProps: FormControlOwnProps = {
  size: 'small',
}
const selectStyles = {
  backgroundColor: colors.background.secondaryBackground,
  maxWidth: SELECT_WIDTH,
}
const menuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
}

const DEI_PRETTY_LABEL = 'DEI Accessibility'

type TaskFiltersProps = {
  filterValues: DashboardFiltersType
  goalTitles: string[]
  isPriorityFilterChecked: boolean
  onFilterChange: ({ filterType, value }: FilterArgs) => void
  onResetFilters: () => void
  userPriorities: UserSurveyPriority[]
}

export const DashboardFilters = ({
  filterValues,
  goalTitles,
  isPriorityFilterChecked,
  onFilterChange,
  onResetFilters,
  userPriorities,
}: TaskFiltersProps) => {
  const { teamMap = {} } = useNonprofitOrgTeam()
  const { userConfig, nonprofitOrgConfig } = useAppState()

  const { email, id } = userConfig
  const { orgId, platformPlan, funderOrgId } = nonprofitOrgConfig

  const goalCategoryOptions = goalCategories.map(([displayValue, category]) => (
    <MenuItem key={category} value={category}>
      <Checkbox checked={filterValues.categories.indexOf(category) > -1} />
      <ListItemText
        primary={
          displayValue === 'DeiAccessibility'
            ? DEI_PRETTY_LABEL
            : splitAtUppercaseAndAddSpace(displayValue)
        }
      />
    </MenuItem>
  ))

  const goalTitleOptions = goalTitles.map((goalTitle) => (
    <MenuItem key={goalTitle} value={goalTitle}>
      <Checkbox checked={filterValues.goalTitleValues.includes(goalTitle)} />
      <ListItemText primary={goalTitle} />
    </MenuItem>
  ))

  const teamOptions = Object.entries(teamMap).map(
    ([teamMapid, { firstName, lastName }]) => (
      <MenuItem key={teamMapid} value={teamMapid}>
        {firstName} {lastName}
      </MenuItem>
    )
  )

  return (
    <TaskFiltersContainer>
      <Filters>
        <FormControl sx={formControlStyles} {...formControlProps}>
          <InputLabel
            id='tasks-digest-filter-by-task-owner-label'
            sx={inputLabelStyles}
          >
            Filter by Team Member
          </InputLabel>
          <Select
            sx={selectStyles}
            labelId='tasks-digest-filter-by-task-owner-label'
            id='tasks-digest-filter-by-task-owner'
            value={filterValues.taskOwnerId}
            label='Filter by Team Member'
            onChange={(e) => {
              onFilterChange({
                value: e.target.value,
                filterType: 'taskOwnerId',
              })
              dispatchImpactReportTeamFilterClicked({
                funderOrgId: funderOrgId || null,
                orgId,
                platformPlan: platformPlan || '',
                userEmail: email,
                userId: id,
              })
            }}
            MenuProps={menuProps}
          >
            <MenuItem
              value=''
              onClick={() => {
                dispatchImpactReportTeamFilterCleared({
                  funderOrgId: funderOrgId || null,
                  orgId,
                  platformPlan: platformPlan || '',
                  userEmail: email,
                  userId: id,
                })
              }}
            >
              Clear Filter
            </MenuItem>
            <Divider />
            <MenuItem value='unassigned'>Unassigned</MenuItem>
            {teamOptions}
          </Select>
        </FormControl>
        <FormControl sx={formControlStyles} {...formControlProps}>
          <InputLabel
            id='tasks-digest-filter-by-goal-category-label'
            sx={inputLabelStyles}
          >
            Filter by Category
          </InputLabel>
          <Select
            labelId='tasks-digest-filter-by-goal-category-label'
            id='tasks-digest-filter-by-goal-category'
            input={<OutlinedInput label='Filter by Category' />}
            label='Filter by Category'
            MenuProps={menuProps}
            multiple
            onChange={(e) => {
              onFilterChange({
                value: e.target.value,
                filterType: 'categories',
              })
              dispatchImpactReportCategoryFilterClicked({
                funderOrgId: funderOrgId || null,
                category: e.target.value,
                userId: id,
                userEmail: email,
                orgId,
                platformPlan: platformPlan || '',
              })
            }}
            renderValue={(selected) =>
              selected
                .map((s) =>
                  s === 'DEI_ACCESSIBILITY' ? DEI_PRETTY_LABEL : toTitleCase(s)
                )
                .join(', ')
            }
            sx={selectStyles}
            value={filterValues.categories}
          >
            {userPriorities && (
              <MenuItem
                value={PRIORTIES_FILTER_TRIGGER}
                disabled={isPriorityFilterChecked}
                onClick={() => {
                  dispatchImpactReportFilterByPriorityClicked({
                    funderOrgId: funderOrgId || null,
                    orgId,
                    platformPlan: platformPlan || '',
                    userEmail: email,
                    userId: id,
                    priorities: userPriorities,
                  })
                }}
              >
                <Checkbox checked={isPriorityFilterChecked} />
                <ListItemText primary='Filter by Priorities' />
              </MenuItem>
            )}
            <Divider />
            {goalCategoryOptions}
          </Select>
        </FormControl>
        {goalTitleOptions.length > 0 && (
          <FormControl sx={formControlStyles} {...formControlProps}>
            <InputLabel
              id='dashboard-filter-by-goal-title-label'
              sx={inputLabelStyles}
            >
              Filter by Goal Title
            </InputLabel>
            <Select
              sx={selectStyles}
              labelId='dashboard-filter-by-goal-title-label'
              id='dashboard-filter-by-goal-title'
              value={filterValues.goalTitleValues}
              label='Filter by Goal Title'
              multiple
              onChange={(e) => {
                onFilterChange({
                  value: e.target.value,
                  filterType: 'goalTitleValues',
                })
                dispatchImpactReportGoalTitleFilterClicked({
                  funderOrgId: funderOrgId || null,
                  goalTitle: e.target.value,
                  orgId,
                  platformPlan: platformPlan || '',
                  userEmail: email,
                  userId: id,
                })
              }}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={menuProps}
            >
              {goalTitleOptions}
            </Select>
          </FormControl>
        )}
      </Filters>
      <ClearFiltersContainer>
        <Button
          label='Clear Filters'
          onClick={onResetFilters}
          variant='tertiary'
        />
      </ClearFiltersContainer>
    </TaskFiltersContainer>
  )
}
